import { memoEval } from '@salsita-ai/platform-project-sdk/domain'

import { EnhancedModel, Model } from '../__generated/configurator-builder.js'
import { implementCustomExpressions, implementCustomInvariants } from '../__generated/implementation.js'
import type { EdgeStyles } from '../__generated/model/index.js'
import { Finishes, LeafTypes, TopWithWidthAndLength } from '../__generated/model/index.js'
import {
  DISTRESS_SCRATCHES_MAP,
  FINISH_MAP,
  TUMBLED_EDGE_NORMAL_MAP_URL,
  TUMBLED_EDGE_NORMAL_SCALE,
  TUMBLED_EDGE_REPEAT_RATIO,
  WAVY_EDGE_UV_SCALE,
  WOOD_SPECIES_TEXTURE_URLS,
  getWoodMaterialProps,
} from '../data/texture-data.js'
import {
  AVAILABLE_BASE_STYLES_MAP,
  AVAILABLE_LEAF_TYPES_MAP,
  ROGERS_SINGLE_BASE_MAX_LENGTH_MAP,
  THICKNESS_OPTIONS,
  TOP_DISTRESS_LEVEL_OPTIONS,
  TOP_EDGE_STYLE_OPTIONS,
} from '../data/top-data.js'
import { createRecipe } from '../index.js'

export const topExpressions = implementCustomExpressions({
  Top: {
    $customProductTopDescription: s => {
      const topData = s.$topDataMap[s.type as keyof typeof s.$topDataMap]
      const dimensions = s.$topDimensionsHTML

      let description = `<h3>Table Top</h3>\n<ul>`
      description += `<li><strong>Shape:</strong> ${topData.label}</li>`

      // Add dimensions
      dimensions.forEach(dim => {
        description += `<li><strong>${dim.label}:</strong> ${dim.value}</li>`
      })

      // Add edge style
      const edgeStyle = TOP_EDGE_STYLE_OPTIONS[s.edgeStyle as keyof typeof TOP_EDGE_STYLE_OPTIONS]
      description += `<li><strong>Edge Style:</strong> ${edgeStyle.label}</li>`

      // Add distress level
      const distressLevel = TOP_DISTRESS_LEVEL_OPTIONS[s.distressLevel as keyof typeof TOP_DISTRESS_LEVEL_OPTIONS]
      description += `<li><strong>Distress Level:</strong> ${distressLevel.label}${distressLevel.description ? ` - ${distressLevel.description}` : ''}</li>`

      // Add finish
      const finish = FINISH_MAP[s.finish as keyof typeof FINISH_MAP]
      description += `<li><strong>Finish:</strong> ${finish.label}</li>`

      // Add special features
      const features: string[] = []
      if (s.$table.$top.$apron && s.$table.$top.$apron.type !== 'ApronNone') {
        features.push('With Apron')
      }
      if (s.withGrooves) {
        features.push('With Grooves')
      }
      if (features.length > 0) {
        description += `<li><strong>Special Features:</strong> ${features.join(', ')}</li>`
      }

      // Add leaf information
      if (s.leafType !== LeafTypes.NoLeaf) {
        const leafData = s.$leafDataMap[s.leafType as keyof typeof s.$leafDataMap]
        description += `<li><strong>Leaf:</strong> ${s.leafAmount}x ${leafData.label} (${leafData.length}")</li>`
      }

      description += `</ul>`
      return description
    },
    $topDimensionsHTML: s => {
      const dimensions = []

      // Check for different top types and add appropriate dimensions
      if (EnhancedModel.$TopWithWidthAndLength.is(s)) {
        dimensions.push({
          label: 'Width',
          value: `${s.width.toFixed(2)}"`,
        })
        dimensions.push({
          label: 'Length',
          value: `${s.length.toFixed(2)}"`,
        })
      }

      if (EnhancedModel.$TopWithSide.is(s)) {
        dimensions.push({
          label: 'Side',
          value: `${s.side.toFixed(2)}"`,
        })
      }

      if (EnhancedModel.$TopWithDiameter.is(s)) {
        dimensions.push({
          label: 'Diameter',
          value: `${s.diameter.toFixed(2)}"`,
        })
      }

      // Add thickness to dimensions
      dimensions.push({
        label: 'Thickness',
        value: `${s.thickness.toFixed(2)}" (${THICKNESS_OPTIONS[s.thicknessType as keyof typeof THICKNESS_OPTIONS].label})`,
      })

      return dimensions
    },
    $offsetY: s => s.$table.$base.$defaultHeight + s.thickness,
    $leafPerimeterOffsetY: s => (s.$offsetY ?? 0) - 0.1,

    $withBevel: s => s.edgeStyle === Model.EdgeStyles.Beveled,

    $bevelThicknessValue: s => s.thickness - s.$straightPartOfBevelTopThickness,

    $bevelThickness: s => (s.$withBevel ? s.$bevelThicknessValue : 0),

    $thicknessValue: s => {
      const thicknessValue = s.$withBevel ? s.$straightPartOfBevelTopThickness : s.thickness
      if (thicknessValue <= 0) {
        return 0.1
      }
      return thicknessValue
    },

    $thicknessTypeOptions: memoEval(
      s => [s.$availableThicknessTypes],
      availableThicknessTypes => {
        const availableThicknesses = Object.values(THICKNESS_OPTIONS).filter(item =>
          availableThicknessTypes.includes(item.type)
        )
        return availableThicknesses.map(item => ({
          label: item.label,
          value: item.type,
        }))
      }
    ),
    $availableThicknessTypes: memoEval(
      s => [s.type, s.$topDataMap],
      (type, topDataMap) => {
        return topDataMap[type]?.sizing?.thickness?.thicknessTypes ?? []
      }
    ),
    // ----------------------------------Texture URLs----------------------------------
    $textureUrl: s => WOOD_SPECIES_TEXTURE_URLS[s.$table.woodSpecies]?.baseColor,
    $normalMapUrl: s => {
      const textures = WOOD_SPECIES_TEXTURE_URLS[s.$table.woodSpecies]
      return s.withGrooves ? textures?.groovesNormal : textures?.normal
    },
    $edgeNormalMapUrl: s =>
      s.edgeStyle === Model.EdgeStyles.Tumbled
        ? TUMBLED_EDGE_NORMAL_MAP_URL
        : WOOD_SPECIES_TEXTURE_URLS[s.$table.woodSpecies].normal,
    $distressBumpMapUrl: s => DISTRESS_SCRATCHES_MAP[s.distressLevel]?.url ?? '',
    $distressBumpScale: s => DISTRESS_SCRATCHES_MAP[s.distressLevel]?.bumpScale ?? 0,
    $roughnessMapUrl: s => WOOD_SPECIES_TEXTURE_URLS[s.$table.woodSpecies]?.roughness,
    $aoMapUrl: s => WOOD_SPECIES_TEXTURE_URLS[s.$table.woodSpecies]?.ambientOcclusion,
    // ----------------------------------Material Properties----------------------------------
    $materialRoughness: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).roughness,
    $materialMetalness: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).metalness,
    $materialEnvMapIntensity: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).envMapIntensity,
    $materialSpecularIntensity: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).specularIntensity,
    $materialNormalScale: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).normalScale,
    $edgeNormalScale: s =>
      s.edgeStyle === Model.EdgeStyles.Tumbled
        ? TUMBLED_EDGE_NORMAL_SCALE
        : getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).normalScale,
    $materialReflectivity: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).reflectivity,
    // ----------------------------------Texture Offset----------------------------------
    $textureOffsetX: s => {
      let offsetX = WOOD_SPECIES_TEXTURE_URLS[s.$table.woodSpecies]?.textureOffsetX ?? 0
      offsetX += s.withCornerRadius ? -0.011 : 0 // this fixes slight difference in rounded top uv width
      return offsetX
    },
    // ----------------------------------Repeat X and Y----------------------------------
    // Table top texture repeat
    $repeatX: s => {
      const textureSize = s.$table.$woodTextureSize * (s.withCornerRadius ? 0.97 : 1)
      const ratio = textureSize * (s.edgeStyle === Model.EdgeStyles.NaturalWavy ? WAVY_EDGE_UV_SCALE : 1)
      return s.$width / ratio
    },
    $repeatY: s => {
      const ratio = s.$table.$woodTextureSize * (s.edgeStyle === Model.EdgeStyles.NaturalWavy ? WAVY_EDGE_UV_SCALE : 1)
      return s.$length / ratio
    },
    $edgeRepeatX: s => {
      const isTumbled = s.edgeStyle === Model.EdgeStyles.Tumbled
      const isWavy = s.edgeStyle === Model.EdgeStyles.NaturalWavy
      const woodRatio = s.$table.$woodTextureSize * (isWavy ? WAVY_EDGE_UV_SCALE : 1)
      const objectName = s.$topDataMap[s.type].objectName
      // Curved shapes have cylindrical UV unwrap (U = perimeter, V = height)
      if (objectName === 'TOP_ROUND' || objectName === 'TOP_BOAT') {
        const a = s.$width / 2
        const b = s.$length / 2
        const perimeter = Math.PI * (3 * (a + b) - Math.sqrt((3 * a + b) * (a + 3 * b)))
        return perimeter / (isTumbled ? TUMBLED_EDGE_REPEAT_RATIO : woodRatio)
      }
      if (objectName === 'TOP_HALF_CIRCLE') {
        const perimeter = 2 * (s.$length - s.$width) + Math.PI * s.$width
        return perimeter / (isTumbled ? TUMBLED_EDGE_REPEAT_RATIO : woodRatio)
      }
      // Rectangle/Square: planar UV unwrap is unified with the top, so the edge
      // must use the SAME scale as the top to keep the texture continuous across
      // top<->edge. Tumbled uses its own dedicated edge texture and ratio.
      return isTumbled ? s.$width / TUMBLED_EDGE_REPEAT_RATIO : s.$width / woodRatio
    },
    $edgeRepeatY: s => {
      const isTumbled = s.edgeStyle === Model.EdgeStyles.Tumbled
      const isWavy = s.edgeStyle === Model.EdgeStyles.NaturalWavy
      const woodRatio = s.$table.$woodTextureSize * (isWavy ? WAVY_EDGE_UV_SCALE : 1)
      const objectName = s.$topDataMap[s.type].objectName
      // Curved shapes: V maps to edge height
      if (objectName === 'TOP_ROUND' || objectName === 'TOP_BOAT' || objectName === 'TOP_HALF_CIRCLE') {
        return s.thickness / (isTumbled ? TUMBLED_EDGE_REPEAT_RATIO : woodRatio)
      }
      // Rectangle/Square: match top's $repeatY for continuous top<->edge texture.
      return isTumbled ? s.$length / TUMBLED_EDGE_REPEAT_RATIO : s.$length / woodRatio
    },
    // Leaf texture repeat - leaves are rotated 90° so X/Y are swapped (length→X, width→Y)
    $leafRepeatX: s => {
      const ratio = s.$table.$woodTextureSize * (s.edgeStyle === Model.EdgeStyles.NaturalWavy ? WAVY_EDGE_UV_SCALE : 1)
      return s.$leafLength / ratio
    },
    $leafRepeatY: s => {
      const ratio = s.$table.$woodTextureSize * (s.edgeStyle === Model.EdgeStyles.NaturalWavy ? WAVY_EDGE_UV_SCALE : 1)
      return s.$leafWidth / ratio
    },
    // ----------------------------------Finish Color and File URL----------------------------------
    $finishColor: s => FINISH_MAP[s.finish].colorHex,
    $fileUrl: s => s.$topDataMap[s.type].fileUrl,
    $objectName: s => {
      switch (s.edgeStyle) {
        case Model.EdgeStyles.Clean:
        case Model.EdgeStyles.Beveled:
        case Model.EdgeStyles.Tumbled:
          return s.withCornerRadius ? s.$topDataMap[s.type].objectNameCornerRadius : s.$topDataMap[s.type].objectName
        case Model.EdgeStyles.Dupont:
          return s.$topDataMap[s.type].objectNameDupont
        case Model.EdgeStyles.NaturalWavy:
          return s.$topDataMap[s.type].objectNameWavy
      }
    },

    $availableBaseStyles: s => {
      if (EnhancedModel.$TopWithWidthAndLength.is(s) && s.length < s.$minTopLengthForDoubleBase) {
        switch (s.type) {
          case Model.RectangleTop.type:
            return AVAILABLE_BASE_STYLES_MAP[Model.SquareTop.type]
          case Model.EllipticalOvalTop.type:
            return AVAILABLE_BASE_STYLES_MAP[Model.RoundTop.type]
          case Model.RacetrackOvalTop.type:
            return AVAILABLE_BASE_STYLES_MAP[Model.RoundTop.type]
          case Model.BoatShapeTop.type:
            return AVAILABLE_BASE_STYLES_MAP[Model.RoundTop.type]
        }
      }

      const availableBaseStyles = AVAILABLE_BASE_STYLES_MAP[s.type]
      if (!EnhancedModel.$TopWithWidthAndLength.is(s)) return availableBaseStyles

      const rogersSingleBaseMaxLength = ROGERS_SINGLE_BASE_MAX_LENGTH_MAP[s.type]
      if (rogersSingleBaseMaxLength !== undefined && s.length <= rogersSingleBaseMaxLength) {
        return availableBaseStyles.map(baseStyle =>
          baseStyle === Model.RogersBase.type ? Model.RogersSinglePlatformBase.type : baseStyle
        )
      }

      return availableBaseStyles
    },

    $availableLeafTypes: s => [...(AVAILABLE_LEAF_TYPES_MAP[s.type] ?? [])],
    $leafTypeOptions: memoEval(
      s => [s.$availableLeafTypes, s.$leafDataMap],
      (availableLeafTypes, leafDataMap) => {
        const leafTypes = availableLeafTypes as LeafTypes[]

        return leafTypes.map(item => {
          const leafType = leafDataMap[item]
          const leafSizeLabel = leafType.length ? `(${leafType.length}")` : ''
          const leafOptionLabel = `${leafType.label} ${leafSizeLabel}`
          return {
            label: leafOptionLabel,
            value: leafType.type,
          }
        })
      }
    ),
    $leafObjectName: s => {
      const leafData = s.$leafDataMap[s.leafType]!
      if (leafData.type === LeafTypes.Perimeter) {
        return leafData.objectName
      }
      switch (s.edgeStyle) {
        case Model.EdgeStyles.Dupont:
          return leafData.objectNameDupont
        case Model.EdgeStyles.NaturalWavy:
          return leafData.objectNameWavy
        default:
          return leafData.objectName
      }
    },

    $leafLength: s => s.$leafDataMap[s.leafType].length,
    $leafWidth: s => s.$width,
    $leafThickness: s => s.thickness,

    // LEAF SHAPEKEY VALUES
    $leafLengthShapekeyValue: s =>
      s.edgeStyle === Model.EdgeStyles.Dupont || s.edgeStyle === Model.EdgeStyles.NaturalWavy
        ? s.$leafLength
        : s.$leafWidth,
    $leafWidthShapekeyValue: s =>
      s.edgeStyle === Model.EdgeStyles.Dupont || s.edgeStyle === Model.EdgeStyles.NaturalWavy
        ? s.$leafWidth
        : s.$leafLength,
    $leafThicknessShapekeyValue: s => s.$leafThickness - s.$bevelThickness,

    $leafLeftOffsetZ: s => (s.$length / 2 + s.$leafDataMap[s.leafType].length / 2) * -1,
    $leafLeftRotationY: s =>
      s.edgeStyle === Model.EdgeStyles.Dupont || s.edgeStyle === Model.EdgeStyles.NaturalWavy ? Math.PI : Math.PI / 2,
    $leafLeftVisible: s => {
      const leftLeafTypes: LeafTypes[] = [LeafTypes.Standard, LeafTypes.Medium, LeafTypes.Large]

      return leftLeafTypes.includes(s.leafType) && s.leafAmount >= 1
    },
    $leafDupontEdgeOnSideValue: s => (s.edgeStyle === Model.EdgeStyles.Dupont ? 1 : 0),
    $leafWavyEdgeOnSideValue: s => (s.edgeStyle === Model.EdgeStyles.NaturalWavy ? 1 : 0),
    $leafRightOffsetZ: s => s.$length / 2 + s.$leafDataMap[s.leafType].length / 2,
    $leafRightRotationY: s =>
      s.edgeStyle === Model.EdgeStyles.Dupont || s.edgeStyle === Model.EdgeStyles.NaturalWavy ? 0 : Math.PI / 2,
    $leafRightVisible: s => {
      const leftLeafTypes: LeafTypes[] = [LeafTypes.Standard, LeafTypes.Medium, LeafTypes.Large]

      return leftLeafTypes.includes(s.leafType) && s.leafAmount >= 2
    },
    $leafCenterOffsetY: s => s.$offsetY ?? 0,
    $leafCenterOffsetZ: s => (s.leafAmount >= 2 ? -s.$leafLength / 2 : 0),
    $leafCenterRotationY: s =>
      s.edgeStyle === Model.EdgeStyles.Dupont || s.edgeStyle === Model.EdgeStyles.NaturalWavy ? 0 : Math.PI / 2,
    $leafCenter2RotationY: s => s.$leafCenterRotationY! + Math.PI,
    $leafCenterVisible: s =>
      ([LeafTypes.StandardDropIn, LeafTypes.MediumDropIn, LeafTypes.LargeDropIn] as LeafTypes[]).includes(s.leafType),
    $leafCenter2Visible: s =>
      ([LeafTypes.StandardDropIn, LeafTypes.MediumDropIn, LeafTypes.LargeDropIn] as LeafTypes[]).includes(s.leafType) &&
      s.leafAmount >= 2,
    $leafCenter2OffsetZ: s => s.$leafLength / 2,

    // ----------------------------------Perimeter Leaf----------------------------------
    $leafPerimeterObjectName: s => s.$leafDataMap.Perimeter.objectName,
    $leafPerimeterVisible: s => s.leafType === LeafTypes.Perimeter && s.leafAmount >= 1,
    $leafPerimeterInnerDiameter: s => s.$width,
    $leafPerimeterOuterDiameter: s => s.$width + 2 * s.$leafDataMap[s.leafType].length,
    $leafPerimeterThickness: s => s.thickness,
    $leafPerimeterInnerDiameterScaleZero: s => s.$leafDataMap.Perimeter.sizing.innerDiameter.scaleZero,
    $leafPerimeterInnerDiameterScaleOne: s => s.$leafDataMap.Perimeter.sizing.innerDiameter.scaleOne,
    $leafPerimeterOuterDiameterScaleZero: s => s.$leafDataMap.Perimeter.sizing.outerDiameter.scaleZero,
    $leafPerimeterOuterDiameterScaleOne: s => s.$leafDataMap.Perimeter.sizing.outerDiameter.scaleOne,
    $leafPerimeterThicknessScaleZero: s => s.$leafDataMap.Perimeter.sizing.thickness.scaleZero,
    $leafPerimeterThicknessScaleOne: s => s.$leafDataMap.Perimeter.sizing.thickness.scaleOne,
    $leafPerimeterRepeatX: s => {
      const ratio = s.$table.$woodTextureSize * (s.edgeStyle === Model.EdgeStyles.NaturalWavy ? WAVY_EDGE_UV_SCALE : 1)
      return s.$width / ratio
    },
    $leafPerimeterRepeatY: s => {
      const ratio = s.$table.$woodTextureSize * (s.edgeStyle === Model.EdgeStyles.NaturalWavy ? WAVY_EDGE_UV_SCALE : 1)
      return s.$width / ratio
    },

    $leafAmountOptions: memoEval(
      s => [s.leafType, s.$leafDataMap],
      (leafType, leafDataMap) =>
        leafDataMap[leafType].purchasableAmounts.map(amount => ({
          label: amount.toString(),
          value: amount,
        }))
    ),
    $minThickness: s => (s.$withBevel ? s.$minBeveledTopThickness : THICKNESS_OPTIONS[s.thicknessType].min),
    $maxThickness: s => THICKNESS_OPTIONS[s.thicknessType].max,

    // ----------------------------------Dimension Indicators----------------------------------

    // -----------------Top Dimension Indicator X-----------------

    $topIndicatorXVisible: s => s.$table.$fullWidth !== s.$width,
    $topIndicatorXStartX: s => (s.$table.$fullWidth / 2) * -1,
    $topIndicatorXFixedY: s => (s.$offsetY ?? 0) + 0.1,
    $topIndicatorXFixedZ: s => {
      let fixedZ = s.$length / 2
      if (s.$leafLeftVisible) {
        fixedZ += s.$leafDataMap[s.leafType].length
      }
      // if (s.$leafCenterVisible) {
      //   fixedZ += s.$leafDataMap[s.leafType].length / 2
      // }
      return fixedZ * -1
    },
    $topIndicatorXEndX: s => s.$table.$fullWidth / 2,

    // -----------------Top Dimension Indicator Z-----------------
    $topDimensionIndicatorZVisible: s => s.$table.$fullLength !== s.$length,
    $topIndicatorZFixedX: s => (s.$width / 2) * -1,
    $topIndicatorZFixedY: s => s.$offsetY ?? 0,
    $topIndicatorZStartZ: s => {
      let startZ = s.$length / 2
      const leafLength = s.$leafDataMap[s.leafType].length
      if (s.$leafLeftVisible) {
        startZ += leafLength
      }
      if (s.$leafCenterVisible) {
        startZ += (leafLength * s.leafAmount) / 2
      }
      if (s.$leafPerimeterVisible) {
        startZ += s.$leafDataMap.Perimeter.length
      }
      return startZ * -1
    },
    $topIndicatorZEndZ: s => {
      let endZ = s.$length / 2
      const leafLength = s.$leafDataMap[s.leafType].length
      if (s.$leafRightVisible) {
        endZ += leafLength
      }
      if (s.$leafCenterVisible) {
        endZ += (leafLength * s.leafAmount) / 2
      }
      if (s.$leafPerimeterVisible) {
        endZ += s.$leafDataMap.Perimeter.length
      }
      return endZ
    },

    // -----------------Shape Dimension Indicator X-----------------
    $shapeIndicatorXStartX: s => {
      const startX = s.$width / 2
      return startX * -1
    },
    $shapeIndicatorXFixedY: s => {
      return (s.$offsetY ?? 0) + 0.1
    },
    $shapeIndicatorXFixedZ: s => {
      if (EnhancedModel.$TopWithRoundedCorners.is(s)) {
        return 0
      }
      const fixedZ = s.$length / 2
      // if (s.$leafCenterVisible) {
      //   fixedZ += s.$leafDataMap[s.leafType].length / 2
      // }
      return fixedZ * -1
    },
    $shapeIndicatorXEndX: s => {
      const endX = s.$width / 2
      return endX
    },
    $shapeDimensionIndicatorXExtrusionZ: s => {
      let extrusionZ = s.$length / 2 + 4
      if (EnhancedModel.$TopWithSquareCorners.is(s)) {
        extrusionZ = 4
      }
      if (s.$leafLeftVisible) {
        extrusionZ += s.$leafDataMap[s.leafType].length
      }
      if (s.$leafCenterVisible) {
        extrusionZ += (s.$leafDataMap[s.leafType].length * s.leafAmount) / 2
      }
      return extrusionZ * -1
    },

    // -----------------Shape Dimension Indicator Z-----------------
    $shapeDimensionIndicatorZVisible: s => {
      return !s.$leafCenterVisible
    },
    $shapeIndicatorZFixedX: s => {
      if (EnhancedModel.$TopWithRoundedCorners.is(s)) {
        return 0
      }
      return (s.$width / 2) * -1
    },
    $shapeIndicatorZFixedY: s => {
      return (s.$offsetY ?? 0) + 0.1
    },
    $shapeIndicatorZStartZ: s => {
      const startZ = s.$length / 2
      return startZ * -1
    },
    $shapeIndicatorZEndZ: s => {
      const endZ = s.$length / 2
      return endZ
    },
    $shapeDimensionIndicatorZExtrusionX: s => {
      let extrusionX = 4
      if (EnhancedModel.$TopWithRoundedCorners.is(s)) {
        extrusionX = s.$width / 2 + 4
      }
      return extrusionX * -1
    },

    // -----------------Split Table Dimension Indicator Z-----------------

    $splitTableIndicatorZVisible: s => {
      return s.$leafCenterVisible
    },
    // -----------------Split Table Left Dimension Indicator Z-----------------
    $splitTableLeftIndicatorZFixedX: s => {
      if (EnhancedModel.$TopWithRoundedCorners.is(s)) {
        return 0
      }
      return (s.$width / 2) * -1
    },
    $splitTableLeftIndicatorZFixedY: s => {
      return (s.$offsetY ?? 0) + 0.1
    },
    $splitTableLeftIndicatorZStartZ: s => {
      const startZ = (s.$leafDataMap[s.leafType].length * s.leafAmount) / 2
      return startZ * -1
    },
    $splitTableLeftIndicatorZEndZ: s => {
      let endZ = s.$length / 2
      endZ += (s.$leafDataMap[s.leafType].length * s.leafAmount) / 2
      return endZ * -1
    },
    $splitTableLeftDimensionIndicatorZExtrusionX: s => {
      let extrusionX = 4
      if (EnhancedModel.$TopWithRoundedCorners.is(s)) {
        extrusionX = s.$width / 2 + 4
      }
      return extrusionX * -1
    },

    // -----------------Split Table Right Dimension Indicator Z-----------------
    $splitTableRightIndicatorZFixedX: s => {
      if (EnhancedModel.$TopWithRoundedCorners.is(s)) {
        return 0
      }
      return (s.$width / 2) * -1
    },
    $splitTableRightIndicatorZFixedY: s => {
      return (s.$offsetY ?? 0) + 0.1
    },
    $splitTableRightIndicatorZStartZ: s => {
      const startZ = (s.$leafDataMap[s.leafType].length * s.leafAmount) / 2
      return startZ
    },
    $splitTableRightIndicatorZEndZ: s => {
      let endZ = s.$length / 2
      endZ += (s.$leafDataMap[s.leafType].length * s.leafAmount) / 2
      return endZ
    },
    $splitTableRightDimensionIndicatorZExtrusionX: s => {
      let extrusionX = 4
      if (EnhancedModel.$TopWithRoundedCorners.is(s)) {
        extrusionX = s.$width / 2 + 4
      }
      return extrusionX * -1
    },

    // -----------------Leaf Left Dimension Indicator-----------------
    $leafLeftIndicatorZFixedX: s => {
      return (s.$width / 2) * -1
    },
    $leafLeftIndicatorZFixedY: s => {
      return s.$offsetY ?? 0
    },
    $leafLeftIndicatorZStartZ: s => {
      const startZ = s.$length / 2
      return startZ * -1
    },
    $leafLeftIndicatorZEndZ: s => {
      let endZ = s.$length / 2
      const leafLength = s.$leafDataMap[s.leafType].length
      endZ = endZ + leafLength
      return endZ * -1
    },

    // -----------------Leaf Right Dimension Indicator-----------------
    $leafRightIndicatorZFixedX: s => {
      return (s.$width / 2) * -1
    },
    $leafRightIndicatorZFixedY: s => {
      return s.$offsetY ?? 0
    },
    $leafRightIndicatorZStartZ: s => {
      const startZ = s.$length / 2
      return startZ
    },
    $leafRightIndicatorZEndZ: s => {
      let endZ = s.$length / 2
      const leafLength = s.$leafDataMap[s.leafType].length
      endZ = endZ + leafLength
      return endZ
    },

    // -----------------Leaf Center Dimension Indicator-----------------
    $leafCenterIndicatorZFixedX: s => {
      return (s.$width / 2) * -1
    },
    $leafCenterIndicatorZFixedY: s => {
      return s.$offsetY ?? 0
    },
    $leafCenterIndicatorZStartZ: s => {
      return (s.$leafCenterOffsetZ ?? 0) + s.$leafDataMap[s.leafType].length / 2
    },
    $leafCenterIndicatorZEndZ: s => {
      return (s.$leafCenterOffsetZ ?? 0) - s.$leafDataMap[s.leafType].length / 2
    },

    // -----------------Leaf Center 2 Dimension Indicator-----------------
    $leafCenter2IndicatorZFixedX: s => {
      return (s.$width / 2) * -1
    },
    $leafCenter2IndicatorZFixedY: s => {
      return s.$offsetY ?? 0
    },
    $leafCenter2IndicatorZStartZ: s => {
      return (s.$leafCenter2OffsetZ ?? 0) + s.$leafDataMap[s.leafType].length / 2
    },
    $leafCenter2IndicatorZEndZ: s => {
      return (s.$leafCenter2OffsetZ ?? 0) - s.$leafDataMap[s.leafType].length / 2
    },

    $leafLengthScaleZero: s => {
      switch (s.edgeStyle) {
        case Model.EdgeStyles.Dupont:
          return s.$modelDataMap.DupontLeaf.length.scaleZero
        case Model.EdgeStyles.NaturalWavy:
          return s.$modelDataMap.WavyLeaf.length.scaleZero
        default:
          return s.$topDataMap.RectangleTop.sizing.length.scaleZero
      }
    },
    $leafLengthScaleOne: s => {
      switch (s.edgeStyle) {
        case Model.EdgeStyles.Dupont:
          return s.$modelDataMap.DupontLeaf.length.scaleOne
        case Model.EdgeStyles.NaturalWavy:
          return s.$modelDataMap.WavyLeaf.length.scaleOne
        default:
          return s.$topDataMap.RectangleTop.sizing.length.scaleOne
      }
    },
    $leafWidthScaleZero: s => {
      switch (s.edgeStyle) {
        case Model.EdgeStyles.Dupont:
          return s.$modelDataMap.DupontLeaf.width.scaleZero
        case Model.EdgeStyles.NaturalWavy:
          return s.$modelDataMap.WavyLeaf.width.scaleZero
        default:
          return s.$topDataMap.RectangleTop.sizing.width.scaleZero
      }
    },
    $leafWidthScaleOne: s => {
      switch (s.edgeStyle) {
        case Model.EdgeStyles.Dupont:
          return s.$modelDataMap.DupontLeaf.width.scaleOne
        case Model.EdgeStyles.NaturalWavy:
          return s.$modelDataMap.WavyLeaf.width.scaleOne
        default:
          return s.$topDataMap.RectangleTop.sizing.width.scaleOne
      }
    },
    $leafThicknessScaleZero: s => {
      switch (s.edgeStyle) {
        case Model.EdgeStyles.Dupont:
          return s.$modelDataMap.DupontLeaf.thickness.scaleZero
        case Model.EdgeStyles.NaturalWavy:
          return s.$modelDataMap.WavyLeaf.thickness.scaleZero
        default:
          return s.$topDataMap.RectangleTop.sizing.thickness.scaleZero
      }
    },
    $leafThicknessScaleOne: s => {
      switch (s.edgeStyle) {
        case Model.EdgeStyles.Dupont:
          return s.$modelDataMap.DupontLeaf.thickness.scaleOne
        case Model.EdgeStyles.NaturalWavy:
          return s.$modelDataMap.WavyLeaf.thickness.scaleOne
        default:
          return s.$topDataMap.RectangleTop.sizing.thickness.scaleOne
      }
    },
    $thicknessScaleZero: s => {
      if (s.edgeStyle === Model.EdgeStyles.Dupont) {
        return s.$modelDataMap.DupontTop.thickness.scaleZero
      }
      return s.$topDataMap[s.type].sizing.thickness.scaleZero
    },
    $thicknessScaleOne: s => {
      if (s.edgeStyle === Model.EdgeStyles.Dupont) {
        return s.$modelDataMap.DupontTop.thickness.scaleOne
      }
      return s.$topDataMap[s.type].sizing.thickness.scaleOne
    },
    $widthScaleZero: s => s.$topDataMap[s.type].sizing.width.scaleZero,
    $widthScaleOne: s => s.$topDataMap[s.type].sizing.width.scaleOne,
    $lengthScaleZero: s => s.$topDataMap[s.type].sizing.length.scaleZero,
    $lengthScaleOne: s => s.$topDataMap[s.type].sizing.length.scaleOne,
    $apronOptions: () => {
      return [
        {
          label: 'No Apron',
          recipes: [
            createRecipe({
              type: 'ApronNone',
            }),
          ],
        },
        {
          label: 'With Apron',
          recipes: [
            createRecipe({
              type: 'ApronBasic',
            }),
          ],
        },
      ]
    },
  },
})

export const topInvariants = implementCustomInvariants({
  Top: {
    lengthLargerThanWidth: s => (TopWithWidthAndLength.is(s) ? s.$length > s.$width : true),
    baseStyleNotAvailableForThisTop: s => s.$availableBaseStyles.includes(s.$table.$base.type),
    baseTypeMustHaveStyles: s => s.$table.$baseStyleOptions.length > 0,
    leafAmountNotAvailableForThisLeafType: s =>
      (s.$leafDataMap[s.leafType].purchasableAmounts as unknown as number[]).includes(s.leafAmount),
    lengthMightNotBeAvailableForThisTop: s => {
      const length = s.$length
      const lengthMightBeAvailable =
        length >
        (s.$topDataMap[s.type]?.warnings?.lengthMightNotBeAvailableForThisTop?.minValue ?? Number.MAX_SAFE_INTEGER)
      return !lengthMightBeAvailable
    },
    widthMaybeTooBigForDoor: s => {
      const width = s.$width
      const widthMightBeTooBig = width > (s.$topDataMap[s.type]?.notices?.widthMaybeTooBigForDoor?.minValue ?? 0)
      return !widthMightBeTooBig
    },
    thicknessTypeMightNotBeAvailableForThisTop: s => {
      const thicknessType = s.thicknessType
      const thicknessMightNotBeAvailable =
        thicknessType === s.$topDataMap[s.type]?.warnings?.thicknessTypeMightNotBeAvailableForThisTop?.value
      return !thicknessMightNotBeAvailable
    },
    customTopFinishNeedsPersonalizedQuote: s => s.finish !== Finishes.Custom,
    customLeafSizeNeedsPersonalizedQuote: s => s.leafType !== LeafTypes.CustomLeaf,
    lengthTooShortForDoubleBase: s => {
      if (EnhancedModel.$BaseDoubleLeg.is(s.$table.$base)) {
        return s.$length >= s.$minTopLengthForDoubleBase
      }
      return true
    },
    lengthTooShortForPerimeterOrDropInLeaf: s => {
      const leafTypes: LeafTypes[] = [
        LeafTypes.Perimeter,
        LeafTypes.StandardDropIn,
        LeafTypes.MediumDropIn,
        LeafTypes.LargeDropIn,
      ]

      return !(leafTypes.includes(s.leafType) && s.$length < (s.$leafDataMap[s.leafType].minTopLength ?? 0))
    },
    edgeStyleAvailableForThisTop: s =>
      (s.$topDataMap[s.type].fatals?.edgeStyleAvailableForThisTop?.allowedValues as unknown as EdgeStyles[]).includes(
        s.edgeStyle
      ),
    beveledTopNeverRolled: s =>
      !(s.edgeStyle === Model.EdgeStyles.Beveled && s.thicknessType === Model.ThicknessesTypes.Rolled),
    roundedCorners: s =>
      s.withCornerRadius ? s.edgeStyle === Model.EdgeStyles.Clean && EnhancedModel.$TopWithSquareCorners.is(s) : true,
  },
})
